import { css, keyframes } from "styled-components";
import { MOBILE_SHEET_TOP_MARGIN, SAFE_AREA, SHEET_RADIUS, mediaPhone } from "@/shared/theme/breakpoints";
import { hideWhileTyping } from "@/shared/theme/keyboard";

/**
 * Phone sheet geometry shared by DynamicModal and the few dialogs that can't use it: document-style
 * antd `Modal`s (ToS re-acceptance, legal documents), ActionBar's action sheet, and the account
 * page's hand-built bank confirmations (DeleteBankModal, SetDefaultBankModal). New features get
 * these layouts through DynamicModal's `mobile` prop instead.
 */

const sheetSlideUp = keyframes`
	from { transform: translateY(100%); }
	to { transform: translateY(0); }
`;

const sheetSlideDown = keyframes`
	from { transform: translateY(0); }
	to { transform: translateY(100%); }
`;

const sheetFade = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const sheetMaxHeight = `calc(100dvh - ${MOBILE_SHEET_TOP_MARGIN}px - ${SAFE_AREA.top})`;

/** Bottom sheet: full width, rounded top corners, grows with its content up to the top gap. */
export const phoneSheetDialog = css`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 100%;
	max-height: ${sheetMaxHeight};
	border-radius: ${SHEET_RADIUS}px ${SHEET_RADIUS}px 0 0;
	overflow: hidden;
	transform-origin: bottom center;
	animation: ${sheetSlideUp} 240ms cubic-bezier(0.2, 0, 0, 1) forwards;

	@media (prefers-reduced-motion: reduce) {
		animation: ${sheetFade} 160ms ease forwards;
	}
`;

/** Height of the panel docked under a full-screen viewer (DynamicModal `mobile="viewer"`): about half the screen. */
export const PHONE_DOCK_HEIGHT = "52dvh";

/** Full screen: covers the page and the bottom bar. */
export const phoneFullscreenDialog = css`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 100%;
	height: 100vh;
	height: 100dvh;
	max-height: 100dvh;
	padding-top: ${SAFE_AREA.top};
	border-radius: 0;
	overflow: hidden;
	animation: ${sheetFade} 160ms ease forwards;
`;

/**
 * Makes an antd `Modal` that is laid out as a phone sheet slide up from the bottom (and back down
 * on close) instead of using antd's zoom. Apply it to the `.ant-modal` element. The `!important`s
 * beat antd's zoom motion, which sets the starting transform and the animation name.
 */
export const antdSheetMotion = css`
	&.ant-zoom-enter,
	&.ant-zoom-appear {
		transform: translateY(100%) !important;
		opacity: 1 !important;
		animation-duration: 240ms !important;
		animation-timing-function: cubic-bezier(0.2, 0, 0, 1) !important;
	}

	&.ant-zoom-enter.ant-zoom-enter-active,
	&.ant-zoom-appear.ant-zoom-appear-active {
		animation-name: ${sheetSlideUp} !important;
		animation-fill-mode: forwards !important;
	}

	&.ant-zoom-leave.ant-zoom-leave-active {
		animation-name: ${sheetSlideDown} !important;
		animation-duration: 200ms !important;
		animation-timing-function: ease-in !important;
		animation-fill-mode: forwards !important;
	}
`;

/*
 * antd `Modal` variants. Apply to a `styled(Modal)`, whose class lands on
 * `.ant-modal`; the `!important`s override antd's inline `top`/`width`.
 */
const antdModalEdgeToEdge = css`
	position: fixed;
	top: auto !important;
	right: 0;
	bottom: 0;
	left: 0;
	width: 100% !important;
	max-width: 100% !important;
	/* !important: antd's centred modals (.ant-modal-centered .ant-modal) add a margin that outranks this. */
	margin: 0 !important;
	padding: 0;

	${antdSheetMotion}

	.ant-modal-content {
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}
`;

/** Phone bottom sheet at the full sheet height, for tall document-style antd modals. */
export const antdModalPhoneSheet = css`
	${mediaPhone`
		${antdModalEdgeToEdge}

		.ant-modal-content {
			height: ${sheetMaxHeight};
			padding-bottom: ${SAFE_AREA.bottom};
			border-radius: ${SHEET_RADIUS}px ${SHEET_RADIUS}px 0 0;
		}
	`}

	/* The keyboard would lift the footer actions over the field being typed in. */
	${hideWhileTyping(".ant-modal-footer")}
`;
